import React from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '../../ui/Button';
import { toLocalDatetimeInput } from '../../../utils/dateUtils';
import { ShareSettings } from './types';

interface ShareSettingsPanelProps {
  settings: ShareSettings;
  onSettingsChange: (settings: ShareSettings) => void;
  onSave: () => void;
  onDelete: () => void;
  isSaving: boolean;
  compact?: boolean;
}

export const ShareSettingsPanel: React.FC<ShareSettingsPanelProps> = ({
  settings,
  onSettingsChange,
  onSave,
  onDelete,
  isSaving,
  compact = false
}) => {
  const inputClass = compact
    ? 'w-full px-2 py-1.5 border border-neutral-200 rounded-md text-[11px] text-neutral-900 bg-white focus:outline-none focus:ring-1 focus:ring-primary-600'
    : 'w-full p-3 border border-neutral-200 rounded-[10px] text-sm text-neutral-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600';

  const labelClass = compact
    ? 'block text-[10px] font-medium text-neutral-500 mb-1 uppercase'
    : 'block text-xs font-medium text-neutral-500 mb-1.5';

  return (
    <div className={`bg-neutral-50 rounded-xl border border-neutral-200 ${compact ? 'p-3' : 'p-4'}`}>
      <div className={compact ? 'grid grid-cols-2 gap-2' : 'space-y-3'}>
        <div>
          <label htmlFor="share-expires" className={labelClass}>
            {compact ? 'Expires' : 'Link expires'}
          </label>
          <input
            id="share-expires"
            type="datetime-local"
            value={settings.expires_at}
            onChange={(e) => onSettingsChange({ ...settings, expires_at: e.target.value })}
            min={toLocalDatetimeInput(new Date())}
            disabled={isSaving}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="share-max-views" className={labelClass}>
            {compact ? 'Max views' : 'Maximum views'}
          </label>
          <input
            id="share-max-views"
            type="number"
            value={settings.max_access_count}
            onChange={(e) => onSettingsChange({ ...settings, max_access_count: e.target.value })}
            min="1"
            disabled={isSaving}
            placeholder={compact ? '∞' : 'Unlimited'}
            className={`${inputClass} placeholder:text-neutral-400`}
          />
        </div>
      </div>
      <div className={`flex gap-2 ${compact ? 'mt-2' : 'mt-4'}`}>
        <Button
          onClick={onSave}
          disabled={isSaving}
          loading={isSaving}
          variant="primary"
          size={compact ? 'xs' : 'sm'}
          className="flex-1 !bg-neutral-900 hover:!bg-neutral-800"
        >
          {isSaving ? 'Saving...' : compact ? 'Save' : 'Save settings'}
        </Button>
        <button
          type="button"
          onClick={onDelete}
          disabled={isSaving}
          className={`flex items-center justify-center ${compact ? 'w-9 p-2' : 'w-12 p-3'} bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50`}
          aria-label="Delete share link"
        >
          <Trash2 className={compact ? 'w-3.5 h-3.5' : 'w-[18px] h-[18px]'} />
        </button>
      </div>
    </div>
  );
};
